import { DatePipe } from '@angular/common';
import { Component, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { Image } from '@frontend/core';
import { ImageUrlPipe, InsightMarkdownPipe } from '@frontend/shared';
import { TranslocoModule, provideTranslocoScope } from '@jsverse/transloco';
import { Subscription } from 'rxjs';
import { finalize, map, skip } from 'rxjs/operators';
import { reloadOnLocaleChange } from '../../language';
import { LoaderGlobeAnimationDetailComponent } from '../../loaders';
import { PageTitleService } from '../../navigation/title/title.service';
import { ShowBannerComponent } from '../show-banner/show-banner.component';
import { ShowsCreditsComponent } from '../shows-credits/shows-credits.component';
import { ShowsLikeButtonComponent } from '../shows-like-button/shows-like-button.component';
import { ShowsSubscribeButtonComponent } from '../shows-subscribe-button/shows-subscribe-button.component';
import { CHANNEL_NAMES } from '../shows-player';
import { ShowsTranslationFollower } from '../shows-translation';
import { Clip, DEFAULT_SHOWS_DEFINITION, clipPath, Episode, Show, ShowsDefinition, ShowsItemRef } from '../shows.model';
import { ShowsService } from '../shows.service';

const EPISODES_PER_PAGE = 50;

/**
 * One show: `/shows/{show}`, with `?season={number}` to show one season, in order. Without a
 * season, the newest episodes come first. Its appearance, the channels it is published on, its
 * credits and its clips.
 */
@Component({
  selector: 'multisite-show-page',
  templateUrl: './show-page.component.html',
  styles: `
    .episode-image {
      width: 160px;
      aspect-ratio: 16 / 9;
      object-fit: cover;
      flex-shrink: 0;
    }

    .episode-text {
      min-width: 0;
    }

    @media (max-width: 575.98px) {
      .episode-image {
        width: 96px;
      }
    }
  `,
  imports: [DatePipe, RouterModule, TranslocoModule, ImageUrlPipe, InsightMarkdownPipe, LoaderGlobeAnimationDetailComponent, ShowBannerComponent, ShowsCreditsComponent, ShowsLikeButtonComponent, ShowsSubscribeButtonComponent],
  providers: [provideTranslocoScope('shows', 'tools')],
})
export class ShowPageComponent {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private showsService = inject(ShowsService);
  private pageTitleService = inject(PageTitleService);

  readonly definition: ShowsDefinition = this.route.snapshot.data['showsSection'] ?? DEFAULT_SHOWS_DEFINITION;
  readonly clipPath = clipPath;
  readonly channelNames = CHANNEL_NAMES;
  readonly translation = new ShowsTranslationFollower<Show>();

  readonly slug = toSignal(this.route.paramMap.pipe(map((params) => params.get('show') ?? '')), { initialValue: '' });
  readonly season = toSignal(this.route.queryParamMap.pipe(map((params) => (/^\d+$/.test(params.get('season') ?? '') ? Number(params.get('season')) : null))), { initialValue: null });

  show = signal<Show | null>(null);
  episodes = signal<Episode[]>([]);
  clips = signal<Clip[]>([]);
  hasMoreEpisodes = signal(false);
  loading = signal(false);
  loadingEpisodes = signal(false);
  notFound = signal(false);
  failed = signal(false);

  readonly ref = computed<ShowsItemRef>(() => ({ kind: 'show', slug: this.slug() }));
  /**
   * Whether the episodes listed demonstrate any tools. Tools belong to few shows — the Intercultural
   * Toolbox is the one made for them — so the way to all tools appears only where they do.
   */
  readonly hasTools = computed(() => this.episodes().some((episode) => episode.tools.length > 0));

  private episodesPage = 1;
  private episodesSubscription?: Subscription;
  private loadedSlug = '';

  constructor() {
    this.route.paramMap.subscribe(() => this.load());
    // A season chosen or cleared: the show stays, its list changes. The first emission is the
    // page's arrival, which load() has handled.
    this.route.queryParamMap.pipe(skip(1)).subscribe(() => {
      if (this.loadedSlug === this.slug()) {
        this.loadEpisodes(true);
      }
    });
    reloadOnLocaleChange(() => this.load());
  }

  chooseSeason(season: number | null): void {
    this.router.navigate([], { relativeTo: this.route, queryParams: { season }, queryParamsHandling: 'merge' });
  }

  moreEpisodes(): void {
    this.episodesPage++;
    this.loadEpisodes(false);
  }

  /** An episode's cover in the list: its thumbnail, else its square artwork. */
  episodeImage(episode: Episode): Image | null {
    return episode.images.find((image) => image.role === 'thumbnail') ?? episode.images.find((image) => image.role === 'logo_square') ?? null;
  }

  episodeLabel(episode: Episode): string {
    return [episode.season !== null ? 'S' + episode.season : null, episode.number !== null ? 'E' + episode.number : null].filter(Boolean).join(' · ');
  }

  private load(): void {
    const slug = this.slug();

    this.loading.set(true);
    this.notFound.set(false);
    this.failed.set(false);
    this.loadedSlug = slug;

    this.showsService
      .getShow(slug)
      .pipe(finalize(() => this.loading.set(false)))
      .subscribe({
        next: (show) => {
          this.present(show);
          this.translation.follow(this.ref(), show, (translated) =>
            this.present({ ...translated, seasons: show.seasons, crew: show.crew, images: show.images, cultures: show.cultures, topics: show.topics, tags: show.tags }),
          );
        },
        error: (error) => (error?.status === 404 ? this.notFound.set(true) : this.failed.set(true)),
      });

    this.loadEpisodes(true);
    this.showsService.getClips(slug, { pageSize: 12, include: ['images'] }).subscribe({ next: (page) => this.clips.set(page.items), error: () => this.clips.set([]) });
  }

  private present(show: Show): void {
    this.show.set(show);
    this.pageTitleService.setTitle(show.name);
  }

  private loadEpisodes(fromTheStart: boolean): void {
    if (fromTheStart) {
      this.episodesPage = 1;
    }

    const season = this.season();

    this.episodesSubscription?.unsubscribe();
    this.loadingEpisodes.set(true);
    this.episodesSubscription = this.showsService
      .getEpisodes(this.slug(), {
        filter: season !== null ? { season: [season] } : {},
        sort: season !== null ? 'number' : '-published_at',
        page: this.episodesPage,
        pageSize: EPISODES_PER_PAGE,
        include: ['images', 'tools'],
      })
      .pipe(finalize(() => this.loadingEpisodes.set(false)))
      .subscribe({
        next: (page) => {
          this.episodes.set(fromTheStart ? page.items : [...this.episodes(), ...page.items]);
          this.hasMoreEpisodes.set(page.meta.current_page < page.meta.last_page);
        },
        error: () => this.episodes.set(fromTheStart ? [] : this.episodes()),
      });
  }
}
